import { useMemo, useState } from 'react'
import { childCandidates } from '@kronos/domain'
import type { DesignSource } from '@kronos/domain'
import type { ComponentType } from '../../../data'
import { Glyph } from '../glyphs'
import { ItemThumb } from '../ItemArt'

/**
 * Search items to add, with their pictures. The list stays open after a pick
 * and the picked item leaves it (`childCandidates`), so adding four parts is
 * one search and four taps. More of the same part is the stepper on its row.
 */
export function PartSearch({
  types,
  source,
  parentId,
  assigned,
  onAdd,
}: {
  types: readonly ComponentType[]
  source: DesignSource
  /** Empty for an item that does not exist yet. */
  parentId: string
  assigned: readonly string[]
  onAdd: (type: ComponentType) => void
}): JSX.Element {
  const [query, setQuery] = useState('')
  const key = assigned.join(',')
  const candidates = useMemo(
    () => childCandidates({ types, parentTypeId: parentId, assignedTypeIds: key === '' ? [] : key.split(','), query, source }),
    [types, parentId, key, query, source],
  )

  return (
    <div className="ie-search">
      <input
        type="search"
        className="ie-search__input"
        value={query}
        placeholder="Search items to add — name, part number or supplier"
        aria-label="Search items to add"
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          // Escape clears the search first; only an empty one lets the dialog have it.
          if (e.key === 'Escape' && query !== '') {
            e.preventDefault()
            setQuery('')
          }
        }}
      />
      <ul className="ie-candidates">
        {candidates.map((t) => (
          <li key={t.id}>
            <button type="button" className="ie-candidate" onClick={() => onAdd(t)}>
              <ItemThumb imageId={t.imageId} category={t.category} name={t.name} size={36} />
              <span className="ie-candidate__text">
                <span className="ie-candidate__name">{t.name}</span>
                <span className="ie-candidate__meta">
                  {[t.tracking === 'serialized' ? 'Own serial' : `Counted${t.unit && t.unit !== 'each' ? ` in ${t.unit}` : ''}`, t.category, t.partNumber]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
              <span className="ie-candidate__add">
                <Glyph name="plus" />
                Add
              </span>
            </button>
          </li>
        ))}
        {candidates.length === 0 ? (
          <li className="ie-candidates__empty">
            {query.trim() === ''
              ? 'Everything that could go in is already on the list.'
              : 'Nothing matches. An item has to exist before it can go into something — add it from Inventory first.'}
          </li>
        ) : null}
      </ul>
    </div>
  )
}
